/**
 * Financial Intelligence Engine — Core Calculation Engine
 * Single source of truth for all financial projections.
 * Read-only consumer of existing Koredor job/operational data.
 *
 * INTEGRITY GUARANTEES:
 * - Full financial state fingerprinting (job costs + assumptions)
 * - Locked periods REQUIRE a real snapshot (no fallbacks)
 * - reproduceForecast() flags mismatches as integrity issues
 */
import type { Job } from "../types";
import type {
  PeriodForecast, ForecastPeriod, RevenueLine, CostEstimate,
  ForecastRange, ConfidenceMetadata, ConfidenceLevel,
  DataSourceType, Assumption, Partner, PartnerDistribution, DataLineage,
  ReconciliationRecord, ForecastFingerprint, CompletenessScore,
  JobFinancialInput, ReproducibilityResult, ForecastProof, MaskedForecast,
  FIEPermissions,
} from "./types";
import type { FinancialAttribution } from "./attributionTypes";
import { computeAttributionDigest } from "./attributionTypes";
import { getDefaultAssumptions } from "./seedAssumptions";

let _attrSeq = 0;
function attrId(): string { return `ATTR-${++_attrSeq}-${Date.now()}`; }

// ── Helpers ──
function clamp(v: number, min: number, max: number) { return Math.max(min, Math.min(max, v)); }

function buildRange(expected: number, uncertainty: number): ForecastRange {
  const factor = clamp(uncertainty, 0.02, 0.35);
  return {
    low: Math.round(expected * (1 - factor)),
    expected: Math.round(expected),
    high: Math.round(expected * (1 + factor * 0.6)),
  };
}

// ── Deterministic Hash (DJB2a variant) ──
function djb2Hash(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
    hash |= 0; // force 32-bit integer
  }
  return Math.abs(hash);
}

function hashToHex(hash: number): string {
  return hash.toString(16).toUpperCase().padStart(8, "0");
}

// ── Assumption Digest — deterministic hash of full assumption state ──
function computeAssumptionDigest(assumptions: Assumption[]): string {
  // Sort by key for determinism, serialize key+value pairs
  const sorted = [...assumptions].sort((a, b) => a.key.localeCompare(b.key));
  const serialized = sorted.map(a => `${a.key}=${a.value}${a.unit}@v${a.version}`).join("|");
  return `AD-${hashToHex(djb2Hash(serialized))}`;
}

// ── Full Financial State Fingerprint ──
function computeFingerprint(
  periodId: string,
  snapshotId: string,
  jobs: Job[],
  revenueLines: RevenueLine[],
  assumptions: Assumption[],
  costs: CostEstimate[],
  uncertaintyFactor: number,
  completeness: CompletenessScore,
  allAttributions: FinancialAttribution[],
): ForecastFingerprint {
  const relevantJobs = jobs.filter(j => revenueLines.some(r => r.jobId === j.id));
  const jobInputs: JobFinancialInput[] = relevantJobs
    .map(j => ({
      jobId: j.id,
      contractValue: j.financials?.contractValue || 0,
      materialCost: j.financials?.estimatedMaterialCost || 0,
      laborCost: j.financials?.estimatedLaborCost || 0,
      subcontractorPayout: j.financials?.subcontractorPayout || 0,
    }))
    .sort((a, b) => a.jobId.localeCompare(b.jobId));

  const assumptionDigest = computeAssumptionDigest(assumptions);
  const attrDigest = computeAttributionDigest(allAttributions);

  const jobSegment = jobInputs
    .map(j => `${j.jobId}:${j.contractValue}:${j.materialCost}:${j.laborCost}:${j.subcontractorPayout}`)
    .join(";");

  const revenueSegment = revenueLines
    .map(r => `${r.jobId}:${r.contractValue}:${r.sourceType}`)
    .sort()
    .join(";");

  const costSegment = costs
    .map(c => `${c.category}:${c.blended}:${c.sourceType}`)
    .sort()
    .join(";");

  const metaSegment = `${uncertaintyFactor}|${completeness.overall}`;

  const raw = `${periodId}|${snapshotId}|${assumptionDigest}|${attrDigest}|${jobSegment}|${revenueSegment}|${costSegment}|${metaSegment}`;
  const hash = `FP-${hashToHex(djb2Hash(raw))}`;

  return {
    hash,
    periodId,
    assumptionSnapshotId: snapshotId,
    jobInputs,
    assumptionDigest,
    attributionDigest: attrDigest,
    inputCount: jobInputs.length,
    generatedAt: new Date().toISOString(),
  };
}

// ── Revenue Extraction ──
function extractRevenueLines(jobs: Job[], period: ForecastPeriod): RevenueLine[] {
  return jobs
    .filter(j => {
      const d = j.scheduledInstallDate || j.targetInstallDate;
      return d && d >= period.startDate && d <= period.endDate;
    })
    .map(j => {
      const hasContract = !!(j.financials?.contractValue && j.financials.contractValue > 0);
      const sourceType: DataSourceType = hasContract ? "actual" : "estimated";
      const value = j.financials?.contractValue || 0;

      const unresolvedItems: string[] = [];
      if (!hasContract) unresolvedItems.push("No signed contract value");
      if (!j.scheduledInstallDate) unresolvedItems.push("No confirmed install date");
      if (j.permitRequired && j.permitStatus !== "Approved") unresolvedItems.push(`Permit ${j.permitStatus || "unknown"}`);

      const confidence: ConfidenceLevel =
        j.currentStage === "Completed" ? "high" :
        hasContract && j.scheduledInstallDate ? "medium" : "low";

      const revenueAttr: FinancialAttribution = {
        id: attrId(),
        sourceType: "job",
        sourceId: j.id,
        sourceTable: "jobs",
        category: "revenue",
        amount: value,
        isActual: hasContract,
        description: hasContract ? `Signed contract revenue` : `Estimated revenue — no contract`,
        jobId: j.id,
        createdAt: new Date().toISOString(),
      };

      return {
        jobId: j.id,
        customerName: j.customerName,
        projectType: j.projectType,
        contractValue: value,
        sourceType,
        scheduledDate: j.scheduledInstallDate || j.targetInstallDate || "",
        stage: j.currentStage,
        confidence,
        lineage: {
          sourceType,
          description: hasContract
            ? `Contract value from signed agreement`
            : `Estimated value — no signed contract on file`,
          lastUpdated: new Date().toISOString(),
          contributingJobIds: [j.id],
          assumptionsUsed: hasContract ? [] : ["estimated_contract_value"],
          unresolvedItems,
        },
        attributions: [revenueAttr],
      };
    });
}

// ── Cost Estimation ──
function estimateCosts(jobs: Job[], revenueLines: RevenueLine[], assumptions: Assumption[]): CostEstimate[] {
  const getAssumption = (key: string) => assumptions.find(a => a.key === key)?.value || 0;
  const getAssumptionLabel = (key: string) => assumptions.find(a => a.key === key)?.label || key;

  let totalActualMaterial = 0;
  let totalActualLabor = 0;
  let totalActualSub = 0;
  let materialJobCount = 0;
  let laborJobCount = 0;
  const materialJobIds: string[] = [];
  const laborJobIds: string[] = [];
  const subJobIds: string[] = [];
  const missingMaterialJobIds: string[] = [];
  const missingLaborJobIds: string[] = [];

  const relevantJobs = jobs.filter(j => revenueLines.some(r => r.jobId === j.id));
  const totalRevenue = revenueLines.reduce((s, r) => s + r.contractValue, 0);

  for (const j of relevantJobs) {
    if (j.financials?.estimatedMaterialCost && j.financials.estimatedMaterialCost > 0) {
      totalActualMaterial += j.financials.estimatedMaterialCost;
      materialJobCount++;
      materialJobIds.push(j.id);
    } else {
      missingMaterialJobIds.push(j.id);
    }
    if (j.financials?.estimatedLaborCost && j.financials.estimatedLaborCost > 0) {
      totalActualLabor += j.financials.estimatedLaborCost;
      laborJobCount++;
      laborJobIds.push(j.id);
    } else {
      missingLaborJobIds.push(j.id);
    }
    if (j.financials?.subcontractorPayout && j.financials.subcontractorPayout > 0) {
      totalActualSub += j.financials.subcontractorPayout;
      subJobIds.push(j.id);
    }
  }

  const materialRate = getAssumption("default_material_pct") / 100;
  const laborRate = getAssumption("default_labor_pct") / 100;
  const permitDefault = getAssumption("default_permit_cost");
  const overheadRate = getAssumption("overhead_allocation_pct") / 100;
  const contingencyRate = getAssumption("contingency_pct") / 100;

  const blendedMaterial = totalActualMaterial + Math.max(0, (revenueLines.length - materialJobCount)) * (materialRate * (totalRevenue / revenueLines.length || 0));
  const blendedLabor = totalActualLabor + Math.max(0, (revenueLines.length - laborJobCount)) * (laborRate * (totalRevenue / revenueLines.length || 0));

  const permitJobs = relevantJobs.filter(j => j.permitRequired);
  const blendedPermit = permitJobs.length * permitDefault;
  const pendingPermitJobs = permitJobs.filter(j => j.permitStatus !== "Approved");

  const overhead = totalRevenue * overheadRate;
  const contingency = totalRevenue * contingencyRate;

  const makeCost = (
    category: CostEstimate["category"],
    actual: number,
    estimated: number,
    jIds: string[],
    missingJIds: string[],
    assumptionKeys: string[],
    unresolved: string[],
  ): CostEstimate => {
    const blended = actual + estimated;
    const st: DataSourceType = actual > 0 && estimated > 0 ? "blended" : actual > 0 ? "actual" : "estimated";
    const costAttributions: FinancialAttribution[] = [];
    // Actual attributions per job
    for (const jid of jIds) {
      const j = relevantJobs.find(job => job.id === jid);
      const amt = category === "materials" ? (j?.financials?.estimatedMaterialCost || 0)
        : category === "labor" ? (j?.financials?.estimatedLaborCost || 0)
        : category === "subcontractor" ? (j?.financials?.subcontractorPayout || 0) : 0;
      if (amt > 0) {
        costAttributions.push({
          id: attrId(), sourceType: "job", sourceId: jid, sourceTable: "jobs",
          category, amount: amt, isActual: true,
          description: `Actual ${category} cost from job`, jobId: jid,
          createdAt: new Date().toISOString(),
        });
      }
    }
    // Estimated attributions
    if (estimated > 0) {
      const srcType = assumptionKeys.length > 0 ? "assumption" as const : "job" as const;
      costAttributions.push({
        id: attrId(), sourceType: srcType, sourceId: assumptionKeys[0] || category,
        sourceTable: srcType === "assumption" ? "assumptions" : "jobs",
        category, amount: estimated, isActual: false,
        description: `Estimated ${category} from ${srcType === "assumption" ? "benchmark assumption" : "projection"}`,
        createdAt: new Date().toISOString(),
      });
    }
    return {
      category, actual, estimated, blended, sourceType: st,
      lineage: {
        sourceType: st,
        description: `${category}: $${actual.toLocaleString()} actual from ${jIds.length} jobs + $${estimated.toLocaleString()} estimated for ${missingJIds.length} jobs`,
        lastUpdated: new Date().toISOString(),
        contributingJobIds: [...jIds, ...missingJIds],
        assumptionsUsed: estimated > 0 ? assumptionKeys.map(k => getAssumptionLabel(k)) : [],
        unresolvedItems: unresolved,
      },
      attributions: costAttributions,
    };
  };

  return [
    makeCost("materials", totalActualMaterial, blendedMaterial - totalActualMaterial, materialJobIds, missingMaterialJobIds, ["default_material_pct"], missingMaterialJobIds.length > 0 ? [`${missingMaterialJobIds.length} jobs using benchmark material cost`] : []),
    makeCost("labor", totalActualLabor, blendedLabor - totalActualLabor, laborJobIds, missingLaborJobIds, ["default_labor_pct"], missingLaborJobIds.length > 0 ? [`${missingLaborJobIds.length} jobs using benchmark labor cost`] : []),
    makeCost("subcontractor", totalActualSub, 0, subJobIds, [], [], []),
    makeCost("permits", 0, blendedPermit, [], permitJobs.map(j => j.id), ["default_permit_cost"], pendingPermitJobs.length > 0 ? [`${pendingPermitJobs.length} permits not yet approved`] : []),
    makeCost("overhead", 0, overhead, [], [], ["overhead_allocation_pct"], ["Overhead is benchmark-allocated, not actual"]),
    makeCost("contingency", 0, contingency, [], [], ["contingency_pct"], ["Contingency is a risk buffer, not an actual cost"]),
  ];
}

// ── Dynamic Uncertainty Factor ──
function computeUncertaintyFactor(jobs: Job[], revenueLines: RevenueLine[]): number {
  const total = revenueLines.length;
  if (total === 0) return 0.35;

  const relevantJobs = jobs.filter(j => revenueLines.some(r => r.jobId === j.id));

  const missingCostJobs = relevantJobs.filter(j =>
    !j.financials?.estimatedMaterialCost || !j.financials?.estimatedLaborCost
  ).length;
  const missingCostRatio = missingCostJobs / total;

  const noContractJobs = revenueLines.filter(r => r.sourceType !== "actual").length;
  const noContractRatio = noContractJobs / total;

  const permitRequired = relevantJobs.filter(j => j.permitRequired);
  const permitsApproved = permitRequired.filter(j => j.permitStatus === "Approved").length;
  const permitUnapprovedRatio = permitRequired.length > 0
    ? (permitRequired.length - permitsApproved) / permitRequired.length
    : 0;

  const changeOrderJobs = relevantJobs.filter(j => (j as any).changeOrders?.length > 0 || (j as any).hasOpenChangeOrder).length;
  const changeOrderRatio = changeOrderJobs / total;

  const uncertainty =
    (missingCostRatio * 0.30) +
    (noContractRatio * 0.30) +
    (permitUnapprovedRatio * 0.20) +
    (changeOrderRatio * 0.20);

  return 0.05 + uncertainty * 0.30;
}

// ── Completeness Score ──
function computeCompleteness(jobs: Job[], revenueLines: RevenueLine[]): CompletenessScore {
  const total = revenueLines.length;
  if (total === 0) return { overall: 0, contractCoverage: 0, costCompleteness: 0, scheduleCertainty: 0, permitCompleteness: 0 };

  const relevantJobs = jobs.filter(j => revenueLines.some(r => r.jobId === j.id));

  const contractCoverage = Math.round((revenueLines.filter(r => r.sourceType === "actual").length / total) * 100);

  const withCosts = relevantJobs.filter(j =>
    j.financials?.estimatedMaterialCost && j.financials.estimatedMaterialCost > 0 &&
    j.financials?.estimatedLaborCost && j.financials.estimatedLaborCost > 0
  ).length;
  const costCompleteness = Math.round((withCosts / total) * 100);

  const scheduled = relevantJobs.filter(j => j.scheduledInstallDate).length;
  const scheduleCertainty = Math.round((scheduled / total) * 100);

  const permitRequired = relevantJobs.filter(j => j.permitRequired);
  const permitsComplete = permitRequired.filter(j => j.permitStatus === "Approved").length;
  const permitCompleteness = permitRequired.length > 0
    ? Math.round((permitsComplete / permitRequired.length) * 100)
    : 100;

  const overall = Math.round(
    contractCoverage * 0.30 +
    costCompleteness * 0.30 +
    scheduleCertainty * 0.25 +
    permitCompleteness * 0.15
  );

  return { overall, contractCoverage, costCompleteness, scheduleCertainty, permitCompleteness };
}

// ── Confidence Scoring ──
function computeConfidence(jobs: Job[], revenueLines: RevenueLine[], costs: CostEstimate[]): ConfidenceMetadata {
  const factors: ConfidenceMetadata["factors"] = [];
  const totalJobs = revenueLines.length;
  if (totalJobs === 0) return { level: "low", score: 0, factors: [{ factor: "No jobs", impact: "negative", description: "No scheduled jobs in period" }] };

  const relevantJobs = jobs.filter(j => revenueLines.some(r => r.jobId === j.id));

  const contractBacked = revenueLines.filter(r => r.sourceType === "actual").length;
  const contractRatio = contractBacked / totalJobs;
  factors.push({ factor: "Contract coverage", impact: contractRatio > 0.7 ? "positive" : contractRatio > 0.4 ? "neutral" : "negative", description: `${contractBacked}/${totalJobs} jobs have signed contracts` });

  const actualCostCategories = costs.filter(c => c.actual > 0).length;
  const totalCategories = costs.length;
  factors.push({ factor: "Cost actuals", impact: actualCostCategories >= 3 ? "positive" : actualCostCategories >= 1 ? "neutral" : "negative", description: `${actualCostCategories}/${totalCategories} cost categories have actual data` });

  const highConfJobs = revenueLines.filter(r => r.confidence === "high").length;
  factors.push({ factor: "Job readiness", impact: highConfJobs / totalJobs > 0.5 ? "positive" : "neutral", description: `${highConfJobs} jobs are completed/confirmed` });

  const scheduledJobs = relevantJobs.filter(j => j.scheduledInstallDate);
  const scheduleRatio = scheduledJobs.length / totalJobs;
  factors.push({ factor: "Schedule stability", impact: scheduleRatio > 0.8 ? "positive" : scheduleRatio > 0.5 ? "neutral" : "negative", description: `${scheduledJobs.length}/${totalJobs} jobs have confirmed install dates` });

  const permitRequired = relevantJobs.filter(j => j.permitRequired);
  const permitsApproved = permitRequired.filter(j => j.permitStatus === "Approved").length;
  const permitRatio = permitRequired.length > 0 ? permitsApproved / permitRequired.length : 1;
  factors.push({ factor: "Permit status", impact: permitRequired.length === 0 ? "neutral" : permitRatio > 0.8 ? "positive" : permitRatio > 0.5 ? "neutral" : "negative", description: permitRequired.length === 0 ? "No permits required for this period" : `${permitsApproved}/${permitRequired.length} required permits approved` });

  const hasChangeOrders = relevantJobs.filter(j => (j as any).changeOrders?.length > 0 || (j as any).hasOpenChangeOrder);
  factors.push({ factor: "Change orders", impact: hasChangeOrders.length === 0 ? "positive" : hasChangeOrders.length <= 2 ? "neutral" : "negative", description: hasChangeOrders.length === 0 ? "No open change orders affecting forecast" : `${hasChangeOrders.length} jobs have open change orders` });

  const score = Math.round(
    (contractRatio * 25) +
    ((actualCostCategories / totalCategories) * 20) +
    ((highConfJobs / totalJobs) * 15) +
    (scheduleRatio * 20) +
    (permitRatio * 10) +
    ((hasChangeOrders.length === 0 ? 1 : Math.max(0, 1 - hasChangeOrders.length / totalJobs)) * 10)
  );
  const level: ConfidenceLevel = score >= 70 ? "high" : score >= 40 ? "medium" : "low";

  return { level, score, factors };
}

// ── Count unresolved items across all lineages ──
function countUnresolvedItems(revenueLines: RevenueLine[], costs: CostEstimate[]): number {
  let count = 0;
  for (const r of revenueLines) {
    count += r.lineage.unresolvedItems?.length || 0;
  }
  for (const c of costs) {
    count += c.lineage.unresolvedItems?.length || 0;
  }
  return count;
}

// ── Main Forecast Generator ──
// INVARIANT: For locked periods, snapshotId MUST be a real snapshot ID (not "SNAP-live")
export function generateForecast(
  jobs: Job[],
  period: ForecastPeriod,
  assumptions: Assumption[],
  snapshotId: string,
  snapshotCreatedAt?: string,
  snapshotSource?: "auto" | "manual",
): PeriodForecast {
  // STRICT ENFORCEMENT: locked periods cannot use live assumptions
  const isLocked = period.status === "closed" || period.status === "reconciled";
  if (isLocked && (snapshotId === "SNAP-live" || !snapshotId)) {
    throw new Error(
      `INTEGRITY VIOLATION: Period "${period.id}" is ${period.status} but no valid snapshot provided. ` +
      `Locked periods MUST have a real assumption snapshot. Cannot proceed.`
    );
  }

  const revenueLines = extractRevenueLines(jobs, period);
  const totalRevenue = revenueLines.reduce((s, r) => s + r.contractValue, 0);
  const costs = estimateCosts(jobs, revenueLines, assumptions);
  const totalCostExpected = costs.reduce((s, c) => s + c.blended, 0);
  const confidence = computeConfidence(jobs, revenueLines, costs);
  const completeness = computeCompleteness(jobs, revenueLines);
  const uncertaintyFactor = computeUncertaintyFactor(jobs, revenueLines);

  const revenue = buildRange(totalRevenue, uncertaintyFactor * 0.5);
  const totalCosts = buildRange(totalCostExpected, uncertaintyFactor);
  const grossProfit: ForecastRange = {
    low: revenue.low - totalCosts.high,
    expected: revenue.expected - totalCosts.expected,
    high: revenue.high - totalCosts.low,
  };
  const netProfit = grossProfit;
  const distributableAmount = netProfit;

  // Collect all attributions for fingerprint
  const allAttributions: FinancialAttribution[] = [
    ...revenueLines.flatMap(r => r.attributions),
    ...costs.flatMap(c => c.attributions),
  ];

  const fingerprint = computeFingerprint(period.id, snapshotId, jobs, revenueLines, assumptions, costs, uncertaintyFactor, completeness, allAttributions);
  const unresolvedItemsCount = countUnresolvedItems(revenueLines, costs);

  const proof: ForecastProof = {
    reproducible: false, // pending until reproduceForecast() verifies
    fingerprint: fingerprint.hash,
    snapshotId,
    verifiedAt: fingerprint.generatedAt,
  };

  return {
    period,
    revenue,
    revenueLines,
    costs,
    totalCosts,
    grossProfit,
    netProfit,
    distributableAmount,
    confidence,
    completeness,
    generatedAt: fingerprint.generatedAt,
    assumptionSnapshotId: snapshotId,
    snapshotCreatedAt,
    snapshotSource,
    fingerprint,
    unresolvedItemsCount,
    integrityStatus: "pending",
    proof,
  };
}

// ── Reproduce Forecast & Verify Integrity ──
export function reproduceForecast(
  jobs: Job[],
  period: ForecastPeriod,
  snapshotAssumptions: Assumption[],
  originalFingerprint: ForecastFingerprint,
): ReproducibilityResult {
  const forecast = generateForecast(
    jobs, period, snapshotAssumptions,
    originalFingerprint.assumptionSnapshotId,
  );
  const matches = forecast.fingerprint.hash === originalFingerprint.hash;
  forecast.integrityStatus = matches ? "verified" : "mismatch";
  forecast.proof = {
    reproducible: matches,
    fingerprint: forecast.fingerprint.hash,
    snapshotId: originalFingerprint.assumptionSnapshotId,
    verifiedAt: new Date().toISOString(),
    ...(matches ? {} : { mismatchDetails: `Original: ${originalFingerprint.hash}, Reproduced: ${forecast.fingerprint.hash}` }),
  };

  return {
    forecast,
    matches,
    originalHash: originalFingerprint.hash,
    reproducedHash: forecast.fingerprint.hash,
    integrityStatus: matches ? "verified" : "mismatch",
  };
}

// ── Data-Layer Permission Masking ──
// Security does NOT rely on UI — this strips data before it reaches any component.
export function maskForecastForRole(
  forecast: PeriodForecast,
  permissions: FIEPermissions,
  partnerId?: string,
  distributions?: PartnerDistribution[],
): MaskedForecast {
  const masked: MaskedForecast = {
    period: forecast.period,
    confidence: forecast.confidence,
    completeness: forecast.completeness,
    proof: forecast.proof,
    integrityStatus: forecast.integrityStatus,
    generatedAt: forecast.generatedAt,
  };

  // Full financial access — executive/finance roles
  if (permissions.viewCompanyWideEconomics) {
    masked.revenue = forecast.revenue;
    masked.revenueLines = forecast.revenueLines;
    masked.totalCosts = forecast.totalCosts;
    masked.grossProfit = forecast.grossProfit;
    masked.netProfit = forecast.netProfit;
    masked.distributableAmount = forecast.distributableAmount;
    masked.fingerprint = forecast.fingerprint;
  }

  // Cost breakdown — available to ops_finance and above
  if (permissions.viewCostBreakdown) {
    masked.costs = forecast.costs;
    if (!masked.totalCosts) masked.totalCosts = forecast.totalCosts;
  }

  // Own-view-only partner — gets ONLY their own distribution row
  if (permissions.viewOwnProjectionOnly && partnerId && distributions) {
    const own = distributions.find(d => d.partnerId === partnerId);
    if (own) {
      masked.ownDistribution = own;
    }
    // Explicitly: no revenue, no costs, no totals, no other distributions
    // No fingerprint (prevents inference from hash changes)
  }

  return masked;
}

// ── Partner Distribution Calculator ──
export function calculateDistributions(
  forecast: PeriodForecast,
  partners: Partner[],
): PartnerDistribution[] {
  return partners.map(p => {
    const pct = p.ownershipPercentage / 100;
    const projectedPayout: ForecastRange = {
      low: Math.round(forecast.distributableAmount.low * pct),
      expected: Math.round(forecast.distributableAmount.expected * pct),
      high: Math.round(forecast.distributableAmount.high * pct),
    };

    const actualInputs = forecast.revenueLines.filter(r => r.sourceType === "actual").length;
    const totalInputs = forecast.revenueLines.length;
    const actualInputPercentage = totalInputs > 0 ? Math.round((actualInputs / totalInputs) * 100) : 0;

    const allUnresolved = forecast.costs.flatMap(c => c.lineage.unresolvedItems || []);

    // Inherit forecast attributions, scaled by ownership
    const allForecastAttrs = [
      ...forecast.revenueLines.flatMap(r => r.attributions),
      ...forecast.costs.flatMap(c => c.attributions),
    ];
    const distAttributions: FinancialAttribution[] = allForecastAttrs.map(a => ({
      ...a,
      id: attrId(),
      amount: Math.round(a.amount * pct),
      description: `${a.description} (${p.ownershipPercentage}% share)`,
    }));

    return {
      partnerId: p.id,
      partnerName: p.name,
      ownershipPercentage: p.ownershipPercentage,
      projectedPayout,
      confidence: forecast.confidence,
      contributingJobCount: totalInputs,
      actualInputPercentage,
      lineage: {
        sourceType: "blended" as const,
        description: `Distribution based on ${p.ownershipPercentage}% ownership of projected distributable amount`,
        lastUpdated: forecast.generatedAt,
        contributingJobIds: forecast.revenueLines.map(r => r.jobId),
        assumptionsUsed: forecast.costs.filter(c => c.sourceType !== "actual").map(c => c.category),
        unresolvedItems: allUnresolved.length > 0 ? allUnresolved : undefined,
      },
      attributions: distAttributions,
    };
  });
}

// ── Real Reconciliation from Job Data ──
export function buildReconciliation(
  jobs: Job[],
  closedPeriods: ForecastPeriod[],
  assumptions: Assumption[],
  snapshotId: string,
): ReconciliationRecord[] {
  return closedPeriods
    .filter(p => p.status === "closed" || p.status === "reconciled")
    .map(period => {
      const projected = generateForecast(jobs, period, assumptions, snapshotId);

      const completedJobs = jobs.filter(j => {
        const d = j.scheduledInstallDate || j.targetInstallDate;
        return d && d >= period.startDate && d <= period.endDate && j.currentStage === "Completed";
      });

      const actualRevenue = completedJobs.reduce((s, j) => s + (j.financials?.contractValue || 0), 0);
      const actualMaterials = completedJobs.reduce((s, j) => s + (j.financials?.estimatedMaterialCost || 0), 0);
      const actualLabor = completedJobs.reduce((s, j) => s + (j.financials?.estimatedLaborCost || 0), 0);
      const actualSub = completedJobs.reduce((s, j) => s + (j.financials?.subcontractorPayout || 0), 0);
      const actualCosts = actualMaterials + actualLabor + actualSub;
      const actualNet = actualRevenue - actualCosts;

      const allJobsInPeriod = jobs.filter(j => {
        const d = j.scheduledInstallDate || j.targetInstallDate;
        return d && d >= period.startDate && d <= period.endDate;
      });
      const incompleteCost = completedJobs.filter(j => !j.financials?.estimatedMaterialCost || !j.financials?.estimatedLaborCost).length;

      const revenueComplete = completedJobs.length === allJobsInPeriod.length ? "complete" : completedJobs.length > 0 ? "partial" : "incomplete";
      const costsComplete = incompleteCost === 0 && completedJobs.length > 0 ? "complete" : completedJobs.length > 0 ? "partial" : "incomplete";

      const projRev = projected.revenue.expected;
      const projCost = projected.totalCosts.expected;
      const projNet = projected.netProfit.expected;

      const drivers: string[] = [];
      const nonCompletedCount = allJobsInPeriod.length - completedJobs.length;
      if (nonCompletedCount > 0) drivers.push(`${nonCompletedCount} jobs not yet completed in period`);
      if (incompleteCost > 0) drivers.push(`${incompleteCost} completed jobs missing cost actuals`);
      if (Math.abs(projRev - actualRevenue) > projRev * 0.05) drivers.push("Revenue variance exceeds 5% of projection");

      return {
        periodId: period.id,
        periodLabel: period.label,
        projected: { revenue: projRev, costs: projCost, netProfit: projNet, distributions: projNet },
        actual: { revenue: actualRevenue, costs: actualCosts, netProfit: actualNet, distributions: actualNet },
        varianceDollar: { revenue: actualRevenue - projRev, costs: actualCosts - projCost, netProfit: actualNet - projNet, distributions: actualNet - projNet },
        variancePercent: {
          revenue: projRev > 0 ? Math.round(((actualRevenue - projRev) / projRev) * 1000) / 10 : 0,
          costs: projCost > 0 ? Math.round(((actualCosts - projCost) / projCost) * 1000) / 10 : 0,
          netProfit: projNet > 0 ? Math.round(((actualNet - projNet) / projNet) * 1000) / 10 : 0,
          distributions: projNet > 0 ? Math.round(((actualNet - projNet) / projNet) * 1000) / 10 : 0,
        },
        drivers,
        reconciledAt: new Date().toISOString(),
        reconciledBy: period.status === "reconciled" ? "CFO" : "System (auto)",
        dataCompleteness: { revenue: revenueComplete, costs: costsComplete, netProfit: costsComplete, distributions: costsComplete },
      };
    });
}
